/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { Field, Form, Formik, getIn } from 'formik';
import { Grid } from '@material-ui/core';
import { find, get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@material-ui/icons/Close';
import SubmitModalLayout from '../../../../components/SubmitModalLayout';
import FormikAutocompleteSelect from '../../../../components/Fields/FormikAutocompleteSelect';
import { SaveButtonBlock } from '../../../../components/EditComponents/SaveButtonBlock';
import { useTranslation } from '../../../../src/i18n';
import TimePicker from '../../../../components/Fields/TimePicker';
import TextField from '../../../../components/Fields/TextField';
import {
  MEDIA_ATTRIBUTES_TYPES_IDS,
  MEDIA_TAGS_TYPES_IDS,
  TP,
  VIDEO_ATTRIBUTE_TYPES_IDS,
} from '../../../../constants/consts';
import { dateSendFormat, getAttributeName, getMediaTimeInfo, getOption } from '../../../../utils/common';
import CalendarField from '../../../../components/Fields/CalendarField';
import FormikCheckbox from '../../../../components/Fields/CheckboxField/FormikCheckbox';
import TimeSelectCard from '../TimeSelectCard';
import { DIGITAL_EVENT_TYPES } from '../../constants';
import { selectImageFormatsNames, selectProductionVideoCostTypesOptions } from '../../selectors';
import SelectField from '../../../../components/Fields/SelectField';
import DropzoneInput from '../../../../components/DropzoneInput';
import ImageTag from '../../../../components/ImageTag';
import { colors } from '../../../../constants/styleConstants';
import StepHeader from '../StepHeader';
import {
  commonDigitalEventFields,
  livestreamCardFields,
  livestreamValidationSchema,
  VODCardFields,
  VODValidationSchema,
} from './consts';
import { getWholeList } from '../../../App/actions';
import { MEDIA_ATTRIBUTES } from '../../../../constants/endpointConstants';
import { getProductionImageFormatsAction } from '../../actions';
import { joinMediaFormats } from '../../../../utils/editCommon';

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(2),
    paddingBottom: 200,
  },
  mediaPlayer: {
    height: 200,
    maxWidth: 400,
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  productionInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  card: {
    marginBottom: theme.spacing(4),
  },
  timezoneField: {
    flex: 1,
  },
  thumbnail: {
    maxHeight: '100%',
    position: 'relative',
  },
  thumbnailImg: {
    objectFit: 'contain',
    maxHeight: 200,
    maxWidth: '100%',
  },
  closeIcon: {
    position: 'absolute',
    top: 5,
    right: 5,
    cursor: 'pointer',
    backgroundColor: colors.white,
    borderRadius: '50%',
  },
}));

const typesOptions = {
  [DIGITAL_EVENT_TYPES.LIVESTREAM]: {
    title: {
      edit: `${TP}.FN_EDIT_LIVESTREAM`,
      add: `${TP}.FN_ADD_LIVESTREAM`,
    },
    fields: livestreamCardFields,
    dropzoneTitle: `${TP}.FN_UPLOAD_LIVESTREAM_THUMBNAIL`,
    validationSchema: livestreamValidationSchema,
  },
  [DIGITAL_EVENT_TYPES.VOD]: {
    title: {
      edit: `${TP}.FN_EDIT_VIDEO_ON_DEMAND`,
      add: `${TP}.FN_ADD_VIDEO_ON_DEMAND`,
    },
    fields: VODCardFields,
    dropzoneTitle: `${TP}.FN_UPLOAD_VOD_THUMBNAIL`,
    validationSchema: VODValidationSchema,
  },
};

const getTypeOption = (type, option) => get(typesOptions, `${type}.${option}`);

const getInitialFormValues = (type, recordInfo, liveDates, timezonesOptions, costOptions) => {
  const {
    startTime,
    url,
    timezone,
    isLiveEventConnected,
    performanceDate,
    endTime,
    dateFrom,
    dateTo,
    costType,
  } = getTypeOption(type, 'fields');
  const performanceDateTag = find(recordInfo?.tags, { tagType: { id: MEDIA_TAGS_TYPES_IDS.DATE } });
  const {
    endTime: recordEndTime,
    timezone: recordTimezone,
    startDate: recordStartDate,
    endDate: recordEndDate,
    startTime: recordStartTime,
  } = getMediaTimeInfo(recordInfo);
  const permValues = {
    id: recordInfo?.id,
    [timezone?.name]: find(timezonesOptions, {
      value: recordTimezone,
    }),
    [costType?.name]:
      find(costOptions, {
        slug: getAttributeName(recordInfo, VIDEO_ATTRIBUTE_TYPES_IDS.COST_TYPE, 'slug'),
      }) || getOption(costOptions[0]),
    [url?.name]: get(recordInfo, 'url') || '',
    [dateFrom?.name]: recordStartDate,
    [isLiveEventConnected?.name]: Boolean(performanceDateTag),
    [performanceDate?.name]:
      find(liveDates, {
        id: performanceDateTag?.productiondate?.id,
      }) || null,
  };
  if (type === DIGITAL_EVENT_TYPES.LIVESTREAM) {
    return {
      ...permValues,
      [startTime?.name]: recordStartTime,
    };
  }

  return {
    ...permValues,
    [endTime?.name]: recordEndTime,
    [dateTo?.name]: recordEndDate,
  };
};

const DigitalEventModal = ({
  onClose,
  recordInfo,
  isLoading,
  onSave,
  timezonesOptions,
  production,
  liveDates,
  type,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const FormRef = React.createRef(null);
  const dispatch = useDispatch();
  const costOptions = useSelector(selectProductionVideoCostTypesOptions());
  const imageFormats = useSelector(selectImageFormatsNames());

  React.useEffect(() => {
    if (!imageFormats?.length) {
      dispatch(
        getWholeList({
          endpoint: MEDIA_ATTRIBUTES,
          sagaRoutine: getProductionImageFormatsAction,
          params: {
            queryParams: {
              attribute_type_id: MEDIA_ATTRIBUTES_TYPES_IDS.IMAGE_FORMAT,
            },
          },
        }),
      );
    }
  }, []);

  const [thumbnailSrc, setThumbnailSrc] = React.useState(null);
  const [initialValues, setInitialValues] = React.useState({});

  const {
    startTime,
    endTime,
    url,
    timezone,
    isLiveEventConnected,
    performanceDate,
    dateFrom,
    dateTo,
    costType,
  } = getTypeOption(type, 'fields');

  React.useEffect(() => {
    if (recordInfo?.thumbnail) {
      setThumbnailSrc(recordInfo?.thumbnail?.urls?.original);
    }
    setInitialValues(getInitialFormValues(type, recordInfo, liveDates, timezonesOptions, costOptions));
  }, []);

  const submitFunc = () => {
    // eslint-disable-next-line no-unused-expressions
    FormRef?.current?.submitForm();
  };

  const uploadThumbnail = image => {
    if (image) {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(image.file);
      fileReader.onload = e => {
        setThumbnailSrc(e.target.result);
      };
    }
  };
  const getInitMonth = date => {
    const dateInFormate = new Date(date);
    return new Date(dateInFormate.getFullYear(), dateInFormate.getMonth());
  };
  return (
    <SubmitModalLayout
      isOpened
      onClose={onClose}
      title1={t(recordInfo?.id ? getTypeOption(type, 'title.edit') : getTypeOption(type, 'title.add'))}
      contentClassname={classes.content}
      footer={
        <SaveButtonBlock
          updatedDate={recordInfo?.updatedAt}
          buttonText={`${TP}.m_SAVE`}
          onSaveClick={submitFunc}
          isLoading={isLoading}
        />
      }
    >
      <StepHeader production={production} noWrapper className={classes.productionInfo} />
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          onSave(values);
          setSubmitting(false);
        }}
        innerRef={FormRef}
        validationSchema={getTypeOption(type, 'validationSchema')}
      >
        {({ values, setFieldValue, errors }) => (
          <Form>
            <Grid
              container
              spacing={2}
              style={{
                width: '100%',
                maxWidth: 800,
              }}
            >
              <Grid item container sm={12} xs={12} spacing={2}>
                <Grid item sm={10} xs={12}>
                  <Field
                    name={url?.name}
                    label={t(url.label)}
                    placeholder={t(`${TP}.FN_ENTER_LINK`)}
                    component={TextField}
                  />
                </Grid>
                <Grid item sm={2} xs={12}>
                  <Field
                    name={costType?.name}
                    label={t(costType?.label)}
                    component={SelectField}
                    options={costOptions}
                    iconType="dropdown"
                    staticLabel
                  />
                </Grid>
              </Grid>
              <Grid item>
                <CalendarField
                  name={dateFrom?.name}
                  label={t(dateFrom.label)}
                  onChange={day => setFieldValue(dateFrom?.name, day ? dateSendFormat(day) : '')}
                  selectedDay={getIn(values, dateFrom?.name)}
                  errorText={t(getIn(errors, dateFrom?.name))}
                  hasError={Boolean(getIn(errors, dateFrom?.name))}
                  fromMonth={new Date(1996, 11)}
                  disabledDays={[
                    {
                      after: get(values, dateTo?.name) ? new Date(get(values, dateTo?.name)) : undefined,
                    },
                  ]}
                />
              </Grid>
              {dateTo && (
                <Grid item>
                  <CalendarField
                    name={dateTo?.name}
                    label={t(dateTo.label)}
                    onChange={day => setFieldValue(dateTo?.name, day ? dateSendFormat(day) : '')}
                    selectedDay={getIn(values, dateTo?.name)}
                    errorText={t(getIn(errors, dateTo?.name))}
                    hasError={Boolean(getIn(errors, dateTo?.name))}
                    fromMonth={
                      getIn(values, dateFrom?.name) ? getInitMonth(get(values, dateFrom?.name)) : new Date(1996, 11)
                    }
                    disabledDays={[
                      {
                        before: get(values, dateFrom?.name) ? new Date(get(values, dateFrom?.name)) : undefined,
                      },
                    ]}
                  />
                </Grid>
              )}
              {startTime && (
                <Grid item>
                  <Field
                    name={startTime?.name}
                    component={TimePicker}
                    label={t(startTime.label)}
                    onTimeChange={lsTime => setFieldValue(startTime?.name, lsTime)}
                  />
                </Grid>
              )}
              {endTime && (
                <Grid item>
                  <Field
                    name={endTime?.name}
                    component={TimePicker}
                    label={t(endTime.label)}
                    onTimeChange={newTime => setFieldValue(endTime?.name, newTime)}
                  />
                </Grid>
              )}
              {timezone && (
                <Grid item className={classes.timezoneField}>
                  <Field
                    instanceId={timezone?.name}
                    name={timezone?.name}
                    label={t(timezone.label)}
                    component={FormikAutocompleteSelect}
                    options={timezonesOptions}
                    withFiltering
                  />
                </Grid>
              )}
              <Grid item container spacing={2}>
                <Grid item sm={7} xs={12}>
                  <DropzoneInput
                    type="image"
                    onFileUpload={d => {
                      setFieldValue(commonDigitalEventFields.thumbnail.name, d[0]);
                      uploadThumbnail(d[0]);
                    }}
                    title={t(getTypeOption(type, 'dropzoneTitle'))}
                    subtitle={t(`${TP}.FN_UPLOAD_FILE_FORMAT_TEMPLATE`, {
                      format: joinMediaFormats(imageFormats),
                    })}
                    multiple={false}
                  />
                </Grid>
                {thumbnailSrc && (
                  <Grid item sm={5} xs={12} className={classes.thumbnail}>
                    <CloseIcon
                      className={classes.closeIcon}
                      onClick={() => {
                        setFieldValue(commonDigitalEventFields.thumbnail.name, null);
                        setThumbnailSrc(null);
                      }}
                    />
                    <ImageTag className={classes.thumbnailImg} src={thumbnailSrc} alt={t(`${TP}.m_PHOTO`)} />
                  </Grid>
                )}
              </Grid>
              {liveDates?.length > 0 && (
                <Grid item sm={12} xs={12}>
                  <Field
                    name={isLiveEventConnected?.name}
                    label={t(isLiveEventConnected.label)}
                    component={FormikCheckbox}
                  />
                  {getIn(values, isLiveEventConnected?.name) &&
                    (getIn(values, performanceDate?.name)
                      ? [getIn(values, performanceDate?.name)]
                      : liveDates || []
                    )?.map(performanceTime => (
                      <TimeSelectCard
                        isSelected={getIn(values, performanceDate?.name)?.id === performanceTime?.id}
                        key={performanceTime?.id}
                        performanceTimeInfo={performanceTime}
                        onSelect={isSelected =>
                          setFieldValue(performanceDate?.name, isSelected ? performanceTime : null)
                        }
                        className={classes.card}
                      />
                    ))}
                </Grid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    </SubmitModalLayout>
  );
};

DigitalEventModal.propTypes = {
  recordInfo: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  timezonesOptions: PropTypes.array,
  production: PropTypes.object.isRequired,
  liveDates: PropTypes.array,
  type: PropTypes.oneOf(Object.values(DIGITAL_EVENT_TYPES)).isRequired,
};

DigitalEventModal.defaultProps = {
  isLoading: false,
  timezonesOptions: [],
  liveDates: {},
};

export default DigitalEventModal;
